import {
    cloneElement,
    useCallback,
    useEffect,
    useRef,
    useState,
    type ReactElement,
    type RefObject,
} from 'react';
import type { ITooltip, PlacesType, TooltipRefProps } from 'react-tooltip';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import Button from '~/components/new/Button';
import { z } from '~/utils/zIndex';

// references to all tooltip providers
// this is used to close tooltips programmatically
const tooltipProviders: Record<string, TooltipRefProps> = {};
const DEFAULT_TOOLTIP_ID = 'global-tooltip';

const IS_PLAYWRIGHT = typeof window !== 'undefined' ? navigator.webdriver : false;

export default function Tooltip({
    children,
    title,
    place = 'bottom',
    tooltipId = DEFAULT_TOOLTIP_ID,
    overflowOnly = false,
    overflowingElementSelector,
    className = '',
}: {
    children: ReactElement<any, string | React.JSXElementConstructor<any>>;
    title?: string;
    place?: PlacesType;
    tooltipId?: string;
    overflowOnly?: boolean;
    overflowingElementSelector?: string;
    className?: string;
}) {
    const localRef = useRef<HTMLElement>(null);
    const ref = ((children.props.ref as React.Ref<unknown> | undefined) ??
        localRef) as RefObject<HTMLElement>;
    // cloneElement only forwards the ref to the child, it never reads .current
    // eslint-disable-next-line react-hooks/refs
    const childrenWithRef = children.props.ref ? children : cloneElement(children, { ref });

    const updateTitle = useCallback((title?: string) => {
        if (!ref.current) return;
        if (title) {
            ref.current.setAttribute('data-tooltip-content', title);
        } else {
            ref.current.removeAttribute('data-tooltip-content');
        }
    }, []);

    useEffect(() => {
        if (!ref.current) return;

        const checkOverflow = (el: HTMLElement) =>
            updateTitle(el.scrollWidth > el.clientWidth ? title : undefined);

        const resizeObserver = new ResizeObserver((entries) => {
            requestAnimationFrame(() =>
                entries.forEach((e) => checkOverflow(e.target as HTMLElement)),
            );
        });

        if (overflowOnly) {
            resizeObserver.observe(
                overflowingElementSelector
                    ? (document.querySelector(overflowingElementSelector) ?? ref.current)
                    : ref.current,
            );
        } else {
            updateTitle(title);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [overflowOnly, children]);

    useEffect(() => {
        if (!ref.current) return;

        ref.current.setAttribute('data-tooltip-id', tooltipId);
        ref.current.setAttribute('data-tooltip-place', place);
        ref.current.setAttribute('data-tooltip-position-strategy', 'fixed');
        ref.current.setAttribute('data-tooltip-class-name', `rt_tooltip ${className}`);

        updateTitle(title);

        // close tooltip on click
        const controller = new AbortController();
        ref.current.addEventListener(
            'click',
            () => {
                tooltipProviders[tooltipId]?.close();
            },
            { signal: controller.signal },
        );

        return () => {
            controller.abort();
        };
    }, [title, place, tooltipId, className, children]);

    return childrenWithRef;
}

export type StepType = {
    targetQuerySelector: string;
    description: string;
    title: string;
    place?: PlacesType;
    offset?: number;
};

export type TourStepProps = {
    steps: StepType[];
    show: boolean;
    onEnd?: (skiped?: boolean) => void;
};

export function TourSteps({ steps, show, onEnd }: TourStepProps) {
    const ref = useRef<TooltipRefProps>(null!);
    const tId = useRef<NodeJS.Timeout>(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [stepConfig, setStepConfig] = useState<StepType>();

    const nextStep = () => {
        if (currentStep < steps.length - 1) {
            toStep(currentStep + 1);
        } else {
            ref.current.close();
            onEnd?.();
        }
    };

    const toStep = (stepIdx: number) => {
        ref.current.close();
        clearTimeout(tId.current!);
        tId.current = setTimeout(
            () => {
                setCurrentStep(stepIdx);
                setStepConfig(steps[stepIdx]);
            },
            IS_PLAYWRIGHT ? 0 : 200,
        );
    };

    const discard = () => {
        clearTimeout(tId.current!);
        ref.current.close();
        onEnd?.(true);
    };

    const showStep = (stepConfig: StepType) =>
        ref.current.open({
            anchorSelect: stepConfig.targetQuerySelector,
            content: (
                <div className='flex flex-column gap-2'>
                    <div className='flex gap-4'>
                        <span className='text-core-contrast desktop-body-1-medium flex flex-grow'>
                            {stepConfig.title}
                        </span>
                        <span className='text-core-tertiary desktop-body-1-regular'>
                            {currentStep + 1} of {steps.length}
                        </span>
                    </div>
                    <p className='text-contrast desktop-body-2-regular-lh-20 m-0'>
                        {stepConfig.description}
                    </p>
                    <hr />
                    <div className='flex flex-row flex-grow justify-between gap-2'>
                        {currentStep !== steps.length - 1 && (
                            <Button onClick={discard}>Skip All</Button>
                        )}

                        <Button onClick={nextStep}>
                            {currentStep < steps.length - 1 ? 'Next' : 'Close'}
                        </Button>
                    </div>
                </div>
            ),
            place: stepConfig.place || 'left',
        });

    // main trigger to show a step, acts when stepConfig changes.
    useEffect(() => {
        if (stepConfig !== undefined) {
            showStep(stepConfig);
        }
    }, [stepConfig]);

    // starts or ends based on `show` prop
    useEffect(() => {
        if (show && !ref.current.isOpen) {
            toStep(0);
        }

        if (!show && ref.current.isOpen) {
            ref.current.close();
        }
    }, [show]);

    return (
        <ReactTooltip
            id='tour-steps'
            imperativeModeOnly
            offset={stepConfig?.offset || 10}
            ref={ref}
        />
    );
}

export function TooltipProvider({ id = DEFAULT_TOOLTIP_ID, ...props }: ITooltip) {
    const ref = useRef<TooltipRefProps>(null!);

    // maintain a reference to the tooltip provider
    useEffect(() => {
        tooltipProviders[id] = ref.current;

        return () => {
            delete tooltipProviders[id];
        };
    }, []);

    return (
        <ReactTooltip
            style={{ zIndex: z.tooltip }}
            ref={ref}
            id={id}
            {...props}
        />
    );
}
